// src/pages/User/StudyMode.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { getReviewerById, logStudySession, saveReviewerReadingProgress, submitQuizResult } from "../../services/api";
import SmartText from "../../components/SmartText";
import DOMPurify from "dompurify";

// --- UTILITY: Fisher-Yates Shuffle ---
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// ---------------------------
// âœ… UTILITY: HEAVY-DUTY HTML TO MARKDOWN
// ---------------------------
const htmlToMarkdown = (html) => {
  if (!html || typeof html !== "string") return "";

  let text = html;

  // 1. DECODE HTML ENTITIES
  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // 2. Preserve newlines
  text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // 3. MATH & SUPERSCRIPTS
  text = text.replace(/<sup[^>]*>(.*?)<\/sup>/gi, "^$1");
  text = text.replace(/<sub[^>]*>(.*?)<\/sub>/gi, "_$1");

  // 4. HEADINGS
  text = text.replace(/<h1[^>]*>(.*?)<\/h1>/gi, "\n\n# $1\n\n");
  text = text.replace(/<h2[^>]*>(.*?)<\/h2>/gi, "\n\n## $1\n\n");
  text = text.replace(/<h3[^>]*>(.*?)<\/h3>/gi, "\n\n### $1\n\n");
  text = text.replace(/<h[4-6][^>]*>(.*?)<\/h[4-6]>/gi, "\n\n#### $1\n\n");

  // 5. INLINE FORMATTING
  text = text.replace(/<b[^>]*>(.*?)<\/b>/gi, "**$1**");
  text = text.replace(/<strong[^>]*>(.*?)<\/strong>/gi, "**$1**");
  text = text.replace(/<i[^>]*>(.*?)<\/i>/gi, "*$1*");
  text = text.replace(/<em[^>]*>(.*?)<\/em>/gi, "*$1*");

  // 6. LISTS
  text = text.replace(/<\/(ul|ol)>/gi, "\n\n");
  text = text.replace(/<(ul|ol)[^>]*>/gi, "\n\n");
  text = text.replace(/<li[^>]*>(.*?)<\/li>/gi, "\n- $1");

  // 7. BLOCKS & PARAGRAPHS
  // âœ… IMPORTANT: handle opening div too (prevents "smushed" blocks)
  text = text.replace(/<div[^>]*>/gi, "\n\n");
  text = text.replace(/<br\s*\/?>/gi, "\n\n");
  text = text.replace(/<\/p>/gi, "\n\n");
  text = text.replace(/<\/div>/gi, "\n\n");

  // 8. STRIP REMAINING TAGS
  text = text.replace(/<[^>]+>/g, "");

  // 9. FINAL POLISH
  text = text.replace(/\n\s*\n/g, "\n\n").trim();

  return text;
};

// ======================================================================
// âœ… MATH / ANSWER NORMALIZERS
// ======================================================================
const normalizeLatexDelimiters = (input) => {
  if (input == null) return "";
  let out = String(input);

  // normalize unicode minus & nbsp
  out = out.replace(/\u2212/g, "-").replace(/\u00A0/g, " ");

  // normalize LaTeX delimiters into $ / $$
  out = out.replace(/\\\(/g, "$").replace(/\\\)/g, "$");
  out = out.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$");

  // fix odd number of $
  const dollarCount = (out.match(/\$/g) || []).length;
  if (dollarCount % 2 === 1) {
    if (/\$\s*$/.test(out)) out = out.replace(/\$\s*$/, "");
    else if (/^\s*\$/.test(out)) out = out.replace(/^\s*\$/, "");
  }

  return out;
};

const extractChoiceLetter = (s) => {
  const t = String(s ?? "").trim();
  if (/^[A-H]$/i.test(t)) return t.toUpperCase();
  const m = t.match(/^([A-H])(?:\s*[:.)-]\s*|\s+)/i);
  return m ? m[1].toUpperCase() : null;
};

const stripChoiceLetter = (s) => {
  const t = String(s ?? "").trim();
  if (/^[A-H]$/i.test(t)) return "";
  return t.replace(/^([A-H])(?:\s*[:.)-]\s*|\s+)/i, "").trim();
};

const normalizeForCompare = (s) => {
  let out = normalizeLatexDelimiters(s);
  out = out.replace(/^\s*\${1,2}\s*/g, "").replace(/\s*\${1,2}\s*$/g, "");
  out = out.replace(/\s+/g, " ").trim().toLowerCase();
  return out;
};

const resolveAnswerToOptionText = (answerRaw, optionsRaw) => {
  const options = Array.isArray(optionsRaw) ? optionsRaw : [];
  const answer = String(answerRaw ?? "").trim();
  if (!answer || options.length === 0) return answer;

  const letterMatch = answer.match(/^[A-H]$/i);
  if (letterMatch) {
    const idx = letterMatch[0].toUpperCase().charCodeAt(0) - 65;
    if (idx >= 0 && idx < options.length) return options[idx];
  }

  const numberMatch = answer.match(/^\d+$/);
  if (numberMatch) {
    const idx = Number(numberMatch[0]) - 1;
    if (idx >= 0 && idx < options.length) return options[idx];
  }

  const normAnswer = normalizeForCompare(stripChoiceLetter(answer));
  const exact = options.find((opt) => normalizeForCompare(stripChoiceLetter(opt)) === normAnswer);
  if (exact) return exact;

  const fuzzy = options.filter((opt) => {
    const n = normalizeForCompare(stripChoiceLetter(opt));
    return n.includes(normAnswer) || normAnswer.includes(n);
  });
  if (fuzzy.length === 1) return fuzzy[0];

  return answer;
};
// ======================================================================

// ---------------------------
// âœ… SUMMARY COERCER
// ---------------------------
const coerceToHtmlString = (value) => {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      trimmed.startsWith("{") ||
      trimmed.startsWith("[")
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === "string") return parsed;
        if (parsed && typeof parsed === "object") {
          return (
            parsed.summaryHtml ||
            parsed.summary ||
            parsed.html ||
            parsed.content ||
            parsed.data?.summaryHtml ||
            ""
          );
        }
      } catch {
        /* ignore */
      }
    }
    return value;
  }
  if (typeof value === "object") {
    return (
      value.summaryHtml ||
      value.summary ||
      value.html ||
      value.content ||
      value.data?.summaryHtml ||
      JSON.stringify(value)
    );
  }
  return String(value);
};

const decodeEntities = (s) =>
  String(s || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");

const escapeHtml = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const toHighYieldSummaryHtml = (raw) => {
  const src = decodeEntities(coerceToHtmlString(raw)).trim();
  if (!src) return "";

  const hasStructuredHtml = /<h3[\s>]/i.test(src) && /<li[\s>]/i.test(src);
  if (hasStructuredHtml) return src;

  const plain = src
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|ul|ol|h1|h2|h3|h4|h5|h6)>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/\r/g, "")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{2,}/g, "\n")
    .trim();

  if (!plain) return "";

  const prepared = plain.replace(/\s+(Module\s*\d+\s*:)/gi, "\n$1");
  const lines = prepared.split("\n").map((x) => x.trim()).filter(Boolean);

  const sections = [];
  let current = null;

  for (const line of lines) {
    if (/^Module\s*\d+\s*:/i.test(line)) {
      if (current) sections.push(current);
      current = { title: line, body: [] };
    } else {
      if (!current) current = { title: "Module 1: Key Concepts", body: [] };
      current.body.push(line);
    }
  }
  if (current) sections.push(current);
  if (sections.length === 0) return "";

  const htmlParts = sections.map((sec) => {
    const body = sec.body
      .join(" ")
      .replace(/\b(Detailed Key Concepts|Key Concepts)\s*:/gi, "")
      .trim();

    let items = body
      .split(/\s*(?:\d+\.\s+|•\s+|-\s+)\s*/g)
      .map((x) => x.trim())
      .filter(Boolean);

    if (items.length < 3) {
      items = body
        .split(/(?<=[.!?])\s+(?=[A-Z0-9'"])/g)
        .map((x) => x.trim())
        .filter(Boolean);
    }

    const lis = items
      .slice(0, 10)
      .map((item) => {
        const m = item.match(/^([^:]{2,48}):\s*(.+)$/);
        if (m) {
          return `<li><b>${escapeHtml(m[1])}:</b> ${escapeHtml(m[2])}</li>`;
        }
        return `<li>${escapeHtml(item)}</li>`;
      })
      .join("");

    return `<div style="margin-bottom: 24px;"><h3>${escapeHtml(sec.title)}</h3><ul>${lis}</ul></div>`;
  });

  return htmlParts.join("");
};

// âœ… Match screenshot-like high-yield notes layout
const summaryHtmlClass =
  "text-gray-700 leading-relaxed text-[15px] md:text-[16px] " +
  "max-h-[70vh] overflow-y-auto pr-4 custom-scroll " +
  "tracking-[0.001em] " +
  // headings (descendant selectors so wrapped HTML is styled too)
  "[&_h1]:text-2xl md:[&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mb-3 [&_h1]:mt-5 " +
  "[&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-[#1E40AF] [&_h2]:mb-3 [&_h2]:mt-5 " +
  "[&_h3]:text-xl md:[&_h3]:text-2xl [&_h3]:font-bold [&_h3]:text-[#1E40AF] [&_h3]:mb-3 [&_h3]:mt-6 " +
  // block spacing
  "[&_div]:mb-4 [&_p]:mb-3 " +
  // lists
  "[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 " +
  "[&_li]:leading-relaxed " +
  // bold lead terms in bullets
  "[&_b]:font-bold [&_b]:text-gray-800 [&_strong]:font-bold [&_strong]:text-gray-800";

const summaryMarkdownClass =
  "text-gray-700 leading-relaxed whitespace-pre-wrap " +
  "max-h-[70vh] overflow-y-auto pr-4 custom-scroll";

const StudyMode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const checkEnabled = (val) => {
    if (val === false) return false;
    if (String(val).toLowerCase() === "false") return false;
    return true;
  };

  const reviewerId =
    params.id ||
    location.state?.reviewerId ||
    location.state?.noteData?._id ||
    location.state?.noteData?.id;

  const queryMode = useMemo(() => {
    const m = new URLSearchParams(location.search).get("mode");
    return m || location.state?.mode || null;
  }, [location.search, location.state?.mode]);

  // --- API RESPONSE NORMALIZER ---
  const normalizeApiPayload = (raw) => {
    const candidate = raw && raw.data && raw.status ? raw.data : raw;
    if (candidate?.meta && candidate?.data) return candidate;
    if (candidate?.data?.meta) {
      return { meta: candidate.data.meta, data: candidate.data.data ?? candidate.data };
    }
    return { meta: {}, data: candidate };
  };

  const pickFirst = (...vals) => {
    for (const v of vals) {
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return null;
  };

  // --- STATE INIT ---
  const [noteData, setNoteData] = useState(
    location.state?.noteData || {
      id: reviewerId || null,
      title: "Loading...",
      subject: "â€”",
      verified: false,
      txHash: null,
      config: {},
    }
  );

  const [summaryRaw, setSummaryRaw] = useState("");
  const [flashcards, setFlashcards] = useState([]);
  const originalQuestionsRef = useRef([]);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [activeTab, setActiveTab] = useState("summary");
  const [isEli5, setIsEli5] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [cardIndex, setCardIndex] = useState(0);

  // --- QUIZ STATE ---
  const [quizState, setQuizState] = useState("intro");
  const [quizModeType, setQuizModeType] = useState("practice");
  const [quizIndex, setQuizIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [selectedQuizOption, setSelectedQuizOption] = useState(null);
  const [textAnswer, setTextAnswer] = useState("");
  const [userAnswers, setUserAnswers] = useState({});
  const latestUserAnswersRef = useRef({});
  const [answerStatus, setAnswerStatus] = useState(null);
  const [summaryCheckpointQuestions, setSummaryCheckpointQuestions] = useState([]);
  const [summaryCheckpointIndex, setSummaryCheckpointIndex] = useState(0);
  const [summaryCheckpointVisible, setSummaryCheckpointVisible] = useState(false);
  const [summaryCheckpointSelectedOption, setSummaryCheckpointSelectedOption] = useState(null);
  const [summaryCheckpointTextAnswer, setSummaryCheckpointTextAnswer] = useState("");
  const [summaryCheckpointStatus, setSummaryCheckpointStatus] = useState(null);
  const [summaryScrollTop, setSummaryScrollTop] = useState(0);
  const summaryCheckpointTimeoutRef = useRef(null);
  const summaryCheckpointFirstShownRef = useRef(false);
  const summaryContentRef = useRef(null);
  const readingBookmarkReadyRef = useRef(false);

  // âœ… New State for Submission Logic
  const [isSubmitting, setIsSubmitting] = useState(false);
  const quizStartTimeRef = useRef(null); // Tracks actual total duration

  // ======================================================================
  // âœ… TIME TRACKING LOGIC (Study Logs)
  // ======================================================================
  const startTimeRef = useRef(Date.now());
  const activeTabRef = useRef(activeTab);
  const summaryLogPausedRef = useRef(false);

  // Track how many cards were reviewed in this session
  const cardsReviewedRef = useRef(0);

  const normalizeActivityType = (t) => (t === "flashcards" ? "flashcard" : t);

  const handleLogTime = useCallback(
    (activityType, start, end) => {
      if (!reviewerId) return;

      const durationSeconds = Math.round((end - start) / 1000);
      if (durationSeconds < 2) return;

      // âœ… Backend identifies user via HttpOnly cookie
      const payload = {
        reviewerId: String(reviewerId),
        activityType: normalizeActivityType(activityType),
        startedAt: new Date(start).toISOString(),
        finishedAt: new Date(end).toISOString(),
        durationSeconds,
        // âœ… NEW: Send metadata for Gamification
        itemCount: activityType === "flashcards" ? cardsReviewedRef.current : 0,
      };

      // Reset card counter after logging
      if (activityType === "flashcards") cardsReviewedRef.current = 0;

      // âœ… Never crash UI due to log endpoint
      Promise.resolve(logStudySession(payload)).catch(() => {});
    },
    [reviewerId]
  );

  const pauseSummaryLog = useCallback(() => {
    if (activeTabRef.current !== "summary") return;
    if (summaryLogPausedRef.current) return;
    const now = Date.now();
    handleLogTime("summary", startTimeRef.current, now);
    startTimeRef.current = now;
    summaryLogPausedRef.current = true;
  }, [handleLogTime]);

  const resumeSummaryLog = useCallback(() => {
    if (!summaryLogPausedRef.current) return;
    startTimeRef.current = Date.now();
    summaryLogPausedRef.current = false;
  }, []);

  useEffect(() => {
    if (activeTabRef.current !== activeTab) {
      const now = Date.now();
      if (!(activeTabRef.current === "summary" && summaryLogPausedRef.current)) {
        handleLogTime(activeTabRef.current, startTimeRef.current, now);
      }
      startTimeRef.current = now;
      activeTabRef.current = activeTab;
      if (activeTab !== "summary") summaryLogPausedRef.current = false;
    }
  }, [activeTab, handleLogTime]);

  useEffect(() => {
    return () => {
      if (summaryCheckpointTimeoutRef.current) {
        clearTimeout(summaryCheckpointTimeoutRef.current);
        summaryCheckpointTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const now = Date.now();
      if (document.visibilityState === "hidden") {
        if (!(activeTabRef.current === "summary" && summaryLogPausedRef.current)) {
          handleLogTime(activeTabRef.current, startTimeRef.current, now);
        }
        startTimeRef.current = now;
      } else {
        startTimeRef.current = now;
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      const now = Date.now();
      if (!(activeTabRef.current === "summary" && summaryLogPausedRef.current)) {
        handleLogTime(activeTabRef.current, startTimeRef.current, now);
      }
    };
  }, [handleLogTime]);

  useEffect(() => {
    if (activeTab !== "summary") return;

    const interval = setInterval(() => {
      if (summaryLogPausedRef.current) return;
      const now = Date.now();
      handleLogTime("summary", startTimeRef.current, now);
      startTimeRef.current = now;
    }, 60000);
    return () => clearInterval(interval);
  }, [activeTab, handleLogTime]);


  // Debounced bookmark saves preserve the exact reading position and any
  // in-progress checkpoint without adding a request for every scroll pixel.
  useEffect(() => {
    if (!reviewerId || !readingBookmarkReadyRef.current) return undefined;
    const timer = setTimeout(() => {
      saveReviewerReadingProgress(reviewerId, {
        scrollTop: summaryScrollTop,
        checkpointIndex: summaryCheckpointIndex,
        checkpointVisible: summaryCheckpointVisible,
        selectedOption: summaryCheckpointSelectedOption,
        textAnswer: summaryCheckpointTextAnswer,
        status: summaryCheckpointStatus,
      }).catch(() => {});
    }, 700);
    return () => clearTimeout(timer);
  }, [
    reviewerId,
    summaryScrollTop,
    summaryCheckpointIndex,
    summaryCheckpointVisible,
    summaryCheckpointSelectedOption,
    summaryCheckpointTextAnswer,
    summaryCheckpointStatus,
  ]);

  // ======================================================================

  const generateRandomizedQuiz = (questions) => {
    const shuffledQs = shuffleArray(questions);
    return shuffledQs.map((q) => ({
      ...q,
      options: q.options.length > 0 ? shuffleArray(q.options) : q.options,
    }));
  };

  useEffect(() => {
    const run = async () => {
      if (!reviewerId) {
        setLoading(false);
        setFetchError("Missing reviewer ID.");
        return;
      }
      try {
        setLoading(true);
        setFetchError("");

        // When navigating from a study group, content is pre-loaded to avoid
        // the ownership-locked blockchain fetch. Use it directly.
        let raw;
        if (location.state?.isGroupShared && location.state?.preloadedContent) {
          raw = {
            meta: {
              ...(location.state.noteData || {}),
              topic: location.state.noteData?.title || "Reviewer",
            },
            data: location.state.preloadedContent,
          };
        } else {
          raw = await getReviewerById(reviewerId);
        }

        const { meta, data } = normalizeApiPayload(raw);

        const txHash = pickFirst(meta?.txHash, meta?.blockchain?.txHash);
        const isMinted = Boolean(meta?.verified ?? meta?.blockchain?.isMinted ?? meta?.isMinted);

        setNoteData((prev) => ({
          ...prev,
          id: reviewerId,
          title: meta?.topic || meta?.title || prev.title || "Untitled",
          subject: meta?.subject || prev.subject || "—",
          verified: isMinted,
          txHash: txHash || null,
          config: meta?.config || prev.config || {},
        }));

        const savedProgress = meta?.readingProgress || {};
        const savedScrollTop = Math.max(0, Number(savedProgress.scrollTop) || 0);

        const rawSummaryCandidate =
          (typeof data === "string" ? data : "") ||
          data?.summaryHtml ||
          data?.summary ||
          data?.html ||
          data?.content ||
          "";
        setSummaryRaw(coerceToHtmlString(rawSummaryCandidate));

        // Flashcards
        const rawCards = data?.flashcards || data?.flashCards || data?.cards || [];
        const cards = Array.isArray(rawCards)
          ? rawCards.map((c, idx) => ({
              id: c?.id || c?._id || idx,
              front: normalizeLatexDelimiters(
                htmlToMarkdown(c?.front || c?.term || c?.question || c?.q || c?.title || "")
              ),
              back: normalizeLatexDelimiters(
                htmlToMarkdown(c?.back || c?.definition || c?.answer || c?.a || c?.content || "")
              ),
            }))
          : [];
        setFlashcards(cards);

        // Quiz
        const rawQuiz = data?.quiz || data?.questions || [];
        const questions = Array.isArray(rawQuiz)
          ? rawQuiz.map((q, idx) => {
              const optsRaw = q?.options || q?.choices || q?.answers || [];
              const opts = Array.isArray(optsRaw)
                ? optsRaw
                    .map((o) =>
                      normalizeLatexDelimiters(
                        htmlToMarkdown(typeof o === "string" ? o : o?.text || o?.label || "")
                      )
                    )
                    .filter(Boolean)
                : [];

              return {
                id: idx,
                prompt: normalizeLatexDelimiters(htmlToMarkdown(q?.question || q?.prompt || q?.q || "")),
                options: opts,
                answer: resolveAnswerToOptionText(
                  normalizeLatexDelimiters(
                    htmlToMarkdown(q?.answer || q?.correctAnswer || q?.correct || "")
                  ),
                  opts
                ),
                explanation: normalizeLatexDelimiters(
                  htmlToMarkdown(q?.explanation || q?.reason || "")
                ),
              };
            })
          : [];

        originalQuestionsRef.current = questions;
        setQuizQuestions(generateRandomizedQuiz(questions));
        setSummaryCheckpointQuestions(questions);
        setSummaryCheckpointIndex(Math.min(Math.max(0, Number(savedProgress.checkpointIndex) || 0), questions.length));
        setSummaryCheckpointVisible(Boolean(savedProgress.checkpointVisible));
        setSummaryCheckpointSelectedOption(savedProgress.selectedOption || null);
        setSummaryCheckpointTextAnswer(savedProgress.textAnswer || "");
        setSummaryCheckpointStatus(savedProgress.status || null);
        setSummaryScrollTop(savedScrollTop);
        summaryCheckpointFirstShownRef.current = Boolean(savedProgress.checkpointVisible || savedProgress.checkpointIndex);
        readingBookmarkReadyRef.current = true;
        requestAnimationFrame(() => {
          if (summaryContentRef.current) summaryContentRef.current.scrollTop = savedScrollTop;
        });
        summaryLogPausedRef.current = false;
        // Note: do NOT clear summaryCheckpointTimeoutRef here.
        // The timer is managed by its own effects and should not be
        // cancelled when data re-loads (e.g. React StrictMode double-invoke).

        setQuizState("intro");
        setCardIndex(0);
        setQuizIndex(0);
        setScore(0);
        setUserAnswers({});
      } catch (e) {
        setFetchError(e?.message || "Failed to load reviewer.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [reviewerId]);

  // Keep a stable ref to pauseSummaryLog so the timer never sees it as a dep.
  const pauseSummaryLogRef = useRef(pauseSummaryLog);
  useEffect(() => {
    pauseSummaryLogRef.current = pauseSummaryLog;
  }, [pauseSummaryLog]);

  // Cancel timer only when user leaves the Summary tab.
  useEffect(() => {
    if (activeTab === "summary") return;
    if (summaryCheckpointTimeoutRef.current) {
      clearTimeout(summaryCheckpointTimeoutRef.current);
      summaryCheckpointTimeoutRef.current = null;
    }
  }, [activeTab]);

  // Schedule the checkpoint. Guard with the ref so re-renders never
  // double-schedule or cancel a running countdown.
  useEffect(() => {
    if (activeTab !== "summary") return;
    if (loading || fetchError) return;
    if (summaryCheckpointVisible) return;
    if (summaryCheckpointIndex >= summaryCheckpointQuestions.length) return;
    if (summaryCheckpointTimeoutRef.current) return;

    // First checkpoint: 15 s. Subsequent ones: 3–5 min.
    const delay = summaryCheckpointFirstShownRef.current
      ? Math.floor(Math.random() * (300000 - 180000 + 1)) + 180000
      : 15000;

    summaryCheckpointTimeoutRef.current = setTimeout(() => {
      summaryCheckpointTimeoutRef.current = null;
      pauseSummaryLogRef.current?.();
      setSummaryCheckpointVisible(true);
      setSummaryCheckpointSelectedOption(null);
      setSummaryCheckpointTextAnswer("");
      setSummaryCheckpointStatus(null);
      summaryCheckpointFirstShownRef.current = true;
    }, delay);
    // No cleanup — we never want a re-render to cancel this timer.
    // The timer clears itself (ref = null) when it fires, and the
    // tab-switch effect above cancels it if the user navigates away.
  }, [
    activeTab,
    loading,
    fetchError,
    summaryCheckpointVisible,
    summaryCheckpointIndex,
    summaryCheckpointQuestions.length,
  ]);


  const config = noteData.config || {};
  const hasSummary = checkEnabled(config.summaryEnabled);
  const hasFlashcards = checkEnabled(config.flashcardsEnabled);
  const hasQuiz = checkEnabled(config.quizEnabled);
  const timeLimit = config.timePerQuestion || 30;

  const tabs = useMemo(() => {
    const base = [
      { id: "summary", label: "Summary", icon: "fa-align-left", ok: hasSummary },
      { id: "flashcards", label: "Flashcards", icon: "fa-layer-group", ok: hasFlashcards },
      { id: "quiz", label: "Quiz Mode", icon: "fa-list-check", ok: hasQuiz },
    ];
    return base.filter((t) => t.ok);
  }, [hasSummary, hasFlashcards, hasQuiz]);

  useEffect(() => {
    if (tabs.length === 0) {
      setActiveTab("none");
      return;
    }
    const ids = tabs.map((t) => t.id);
    if (queryMode && ids.includes(queryMode)) setActiveTab(queryMode);
    else setActiveTab(ids[0]);
  }, [queryMode, tabs]);

  // Flashcards keyboard navigation (Arrow keys to change card, Space/Enter to flip)
  useEffect(() => {
    if (activeTab !== "flashcards" || !hasFlashcards || flashcards.length === 0) return undefined;
    const handleKeyDown = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight") {
        setIsFlipped(false);
        setCardIndex((i) => Math.min(i + 1, flashcards.length - 1));
      } else if (e.key === "ArrowLeft") {
        setIsFlipped(false);
        setCardIndex((i) => Math.max(0, i - 1));
      } else if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        setIsFlipped((f) => !f);
        if (!isFlipped) cardsReviewedRef.current += 1;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeTab, hasFlashcards, flashcards.length, isFlipped]);

  const displaySummaryHtml = useMemo(() => toHighYieldSummaryHtml(summaryRaw), [summaryRaw]);

  const summaryIsHtml = useMemo(() => !!displaySummaryHtml, [displaySummaryHtml]);

  const sanitizedSummaryHtml = useMemo(() => {
    if (!displaySummaryHtml) return "";
    return DOMPurify.sanitize(displaySummaryHtml, { USE_PROFILES: { html: true } });
  }, [displaySummaryHtml]);

  const summaryAsMarkdown = useMemo(() => {
    if (!summaryRaw) return "";
    const md = summaryIsHtml ? htmlToMarkdown(displaySummaryHtml) : summaryRaw;
    return normalizeLatexDelimiters(md);
  }, [summaryRaw, summaryIsHtml, displaySummaryHtml]);

  // --- QUIZ TIMER ---
  useEffect(() => {
    let timer;
    if (activeTab === "quiz" && quizState === "answering" && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, quizState, timeLeft]);

  // âœ… Helper: get the "correct option index"
  const getCorrectIndexIfLetter = (question) => {
    const letter = extractChoiceLetter(question?.answer);
    if (!letter) return null;
    const idx = letter.charCodeAt(0) - 65;
    return idx >= 0 ? idx : null;
  };

  const CorrectAnswerNode = ({ q }) => {
    if (!q) return null;
    const idx = getCorrectIndexIfLetter(q);
    if (idx != null && q.options?.length) {
      const letter = String.fromCharCode(65 + idx);
      return (
        <div className="flex items-start gap-2">
          <span className="font-mono font-bold">{letter}:</span>
          <div className="flex-1">
            <SmartText content={q.options[idx] || ""} />
          </div>
        </div>
      );
    }
    return <SmartText content={q.answer || ""} />;
  };

  // --- HELPER: CHECK ANSWER ---
  const checkCorrectness = (question, userAnswer) => {
    if (!question || userAnswer == null) return false;
    const userText = String(userAnswer).trim();
    if (!userText) return false;

    if (question.options?.length > 0) {
      const ansLetter = extractChoiceLetter(question.answer);
      if (ansLetter) {
        const userLetter = extractChoiceLetter(userText);
        if (userLetter) return userLetter === ansLetter;

        const selectedIndex = question.options.findIndex(
          (opt) =>
            normalizeForCompare(stripChoiceLetter(opt)) ===
            normalizeForCompare(stripChoiceLetter(userText))
        );
        if (selectedIndex >= 0 && selectedIndex < 8) {
          return String.fromCharCode(65 + selectedIndex) === ansLetter;
        }
        return false;
      }

      const selectedLetter = extractChoiceLetter(userText);
      const selectedIndex = selectedLetter ? selectedLetter.charCodeAt(0) - 65 : -1;
      const selectedText = selectedIndex >= 0 ? question.options[selectedIndex] ?? "" : userText;
      return (
        normalizeForCompare(stripChoiceLetter(selectedText)) ===
        normalizeForCompare(stripChoiceLetter(question.answer))
      );
    }
    return (
      normalizeForCompare(stripChoiceLetter(userAnswer)) ===
      normalizeForCompare(stripChoiceLetter(question.answer))
    );
  };

  // --- HANDLERS ---
  const startQuiz = (mode) => {
    setQuizModeType(mode);
    setQuizQuestions(generateRandomizedQuiz(originalQuestionsRef.current));
    setQuizIndex(0);
    setScore(0);
    setTimeLeft(timeLimit);
    setSelectedQuizOption(null);
    setTextAnswer("");
    setUserAnswers({});
    latestUserAnswersRef.current = {};
    setAnswerStatus(null);
    setIsSubmitting(false); // Reset submitting flag
    quizStartTimeRef.current = Date.now(); // Start total timer
    setQuizState("answering");
  };

  const handleTimeout = () => {
    if (quizModeType === "practice") {
      setAnswerStatus("wrong");
      setQuizState("feedback");
    } else {
      handleExamSubmit();
    }
  };

  const handlePrimaryAction = () => {
    const currentQ = quizQuestions[quizIndex];
    const hasAnswer =
      currentQ.options.length > 0 ? selectedQuizOption : textAnswer.trim().length > 0;
    if (!hasAnswer) return;

    if (quizModeType === "practice") {
      handlePracticeSubmit();
    } else {
      handleExamSubmit();
    }
  };

  const handlePracticeSubmit = () => {
    const currentQ = quizQuestions[quizIndex];
    const userAns = currentQ.options.length > 0 ? selectedQuizOption : textAnswer;
    const nextUserAnswers = { ...latestUserAnswersRef.current, [quizIndex]: userAns };
    latestUserAnswersRef.current = nextUserAnswers;
    setUserAnswers(nextUserAnswers);

    const isCorrect = checkCorrectness(currentQ, userAns);
    if (isCorrect) setScore((prev) => prev + 1);

    setAnswerStatus(isCorrect ? "correct" : "wrong");
    setQuizState("feedback");
  };

  const finalizeQuizSession = (answersMap) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    let finalScore = 0;
    const attemptsPayload = [];

    quizQuestions.forEach((q, idx) => {
      const answerForQuestion = answersMap[idx];
      const isCorrect = checkCorrectness(q, answerForQuestion);

      if (isCorrect) finalScore++;

      attemptsPayload.push({
        questionIndex: q.id,
        isCorrect,
      });
    });

    setScore(finalScore);
    setQuizState("finished");

    const totalTimeSeconds = quizStartTimeRef.current
      ? Math.round((Date.now() - quizStartTimeRef.current) / 1000)
      : 0;

    const payload = {
      reviewerId: noteData.id,
      mode: "quiz",
      score: finalScore,
      totalQuestions: quizQuestions.length,
      timeSpentSeconds: totalTimeSeconds,
      attempts: attemptsPayload,
    };

    const submit = async () => {
      try {
        await submitQuizResult(payload);
        console.log("Quiz result saved successfully");

        const logPayload = {
          reviewerId: String(noteData.id),
          activityType: "quiz",
          startedAt: new Date(quizStartTimeRef.current).toISOString(),
          finishedAt: new Date().toISOString(),
          durationSeconds: totalTimeSeconds,
          score: finalScore,
          maxScore: quizQuestions.length,
        };
        await logStudySession(logPayload);
      } catch (err) {
        console.error("Failed to save quiz result:", err);
      }
    };

    submit();
  };

  const handlePracticeNext = () => {
    if (quizIndex < quizQuestions.length - 1) {
      setQuizIndex((prev) => prev + 1);
      setQuizState("answering");
      setTimeLeft(timeLimit);
      setSelectedQuizOption(null);
      setTextAnswer("");
      setAnswerStatus(null);
    } else {
      finalizeQuizSession(latestUserAnswersRef.current);
    }
  };

  const handleExamSubmit = () => {
    const currentQ = quizQuestions[quizIndex];
    const userAns = currentQ.options.length > 0 ? selectedQuizOption : textAnswer;
    const nextUserAnswers = { ...latestUserAnswersRef.current, [quizIndex]: userAns };
    latestUserAnswersRef.current = nextUserAnswers;
    setUserAnswers(nextUserAnswers);

    if (quizIndex < quizQuestions.length - 1) {
      setQuizIndex((prev) => prev + 1);
      setQuizState("answering");
      setTimeLeft(timeLimit);
      setSelectedQuizOption(null);
      setTextAnswer("");
      return;
    }

    finalizeQuizSession(nextUserAnswers);
  };

  const currentQuiz = quizQuestions[quizIndex];
  const hasSelectedAnswer =
    currentQuiz?.options.length > 0 ? !!selectedQuizOption : !!textAnswer.trim();
  const currentCard = flashcards[cardIndex];
  const summaryCheckpointCurrent = summaryCheckpointQuestions[summaryCheckpointIndex];
  const hasSummaryCheckpointAnswer =
    summaryCheckpointCurrent?.options?.length > 0
      ? !!summaryCheckpointSelectedOption
      : !!summaryCheckpointTextAnswer.trim();

  const handleSummaryCheckpointSubmit = () => {
    if (!summaryCheckpointCurrent || !hasSummaryCheckpointAnswer) return;
    let userAns;
    if (summaryCheckpointCurrent.options.length > 0) {
      // summaryCheckpointSelectedOption is a letter like "A", "B", etc.
      // Resolve it to the actual option text so checkCorrectness can compare correctly
      // against question.answer (which is already resolved to option text).
      const selectedLetter = summaryCheckpointSelectedOption;
      const selectedIdx = selectedLetter
        ? selectedLetter.toUpperCase().charCodeAt(0) - 65
        : -1;
      userAns =
        selectedIdx >= 0 && selectedIdx < summaryCheckpointCurrent.options.length
          ? summaryCheckpointCurrent.options[selectedIdx]
          : selectedLetter;
    } else {
      userAns = summaryCheckpointTextAnswer;
    }
    const isCorrect = checkCorrectness(summaryCheckpointCurrent, userAns);
    setSummaryCheckpointStatus(isCorrect ? "correct" : "wrong");
    resumeSummaryLog();
  };

  const handleSummaryCheckpointNext = () => {
    setSummaryCheckpointVisible(false);
    setSummaryCheckpointSelectedOption(null);
    setSummaryCheckpointTextAnswer("");
    setSummaryCheckpointStatus(null);
    setSummaryCheckpointIndex((prev) =>
      Math.min(prev + 1, summaryCheckpointQuestions.length)
    );
  };

  const handleSummaryCheckpointPass = () => {
    setSummaryCheckpointVisible(false);
    setSummaryCheckpointSelectedOption(null);
    setSummaryCheckpointTextAnswer("");
    setSummaryCheckpointStatus(null);
    resumeSummaryLog();
  };

  const FeedbackPanel = () => {
    if (quizState !== "feedback") return null;
    const isCorrect = answerStatus === "correct";
    return (
      <div
        role="status"
        aria-live="polite"
        className={`mt-5 rounded-2xl border-2 p-5 text-sm transition-all shadow-xs ${
          isCorrect
            ? "bg-emerald-50 border-emerald-300 text-emerald-900"
            : "bg-amber-50 border-amber-300 text-amber-900"
        }`}
      >
        <div className="font-extrabold text-base flex items-center gap-2.5">
          {isCorrect ? (
            <>
              <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm shadow-xs">
                <i className="fa-solid fa-check" aria-hidden="true"></i>
              </span>
              <span>Well done! Correct answer.</span>
            </>
          ) : (
            <>
              <span className="w-7 h-7 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm shadow-xs">
                <i className="fa-solid fa-lightbulb" aria-hidden="true"></i>
              </span>
              <span>Good try! Let's review this concept:</span>
            </>
          )}
        </div>
        {!isCorrect && (
          <div className="mt-3 p-3 bg-white/90 rounded-xl border border-amber-200">
            <span className="font-bold text-amber-950 text-xs uppercase tracking-wider block mb-1">
              Correct Answer:
            </span>
            <div className="font-medium text-slate-900">
              <CorrectAnswerNode q={currentQuiz} />
            </div>
          </div>
        )}
        {currentQuiz?.explanation && (
          <div className="mt-3 rounded-xl bg-white/90 border border-slate-200 p-3.5 shadow-xs">
            <span className="font-bold text-slate-900 text-xs uppercase tracking-wider block mb-1">
              Explanation &amp; Key Takeaway:
            </span>
            <div className="text-sm leading-relaxed text-slate-700">
              <SmartText content={currentQuiz.explanation} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-slate-50 text-slate-800 font-sans antialiased h-screen flex flex-col overflow-hidden relative">
      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 6px; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .perspective-1000 { perspective: 1000px; }
        .transform-style-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }
      `}</style>

      {/* HEADER */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6 z-20 shrink-0">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition"
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <div className="h-6 w-px bg-gray-200 flex-shrink-0"></div>
          <div className="min-w-0 flex-1">
            <h1 className="font-bold text-gray-900 leading-tight truncate text-sm md:text-base font-[Poppins]">
              {noteData.title}
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-gray-400 truncate font-mono">
                {noteData.subject}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowSidebar(!showSidebar)}
          className="w-8 h-8 flex items-center justify-center rounded-lg border bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
        >
          <i className={`fa-solid ${showSidebar ? "fa-expand" : "fa-compress"}`}></i>
        </button>
      </header>

      {/* BODY */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* SIDEBAR */}
        <aside
          className={`w-full md:w-64 bg-white border-b md:border-b-0 md:border-r border-gray-200 flex-row md:flex-col p-2 gap-2 z-10 shrink-0 overflow-x-auto ${
            showSidebar ? "flex" : "hidden"
          }`}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-none flex items-center gap-3 px-4 py-3 rounded-xl transition text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? "text-blue-600 bg-blue-50 font-bold border border-blue-100"
                  : "text-gray-500 hover:bg-gray-50 font-medium border border-transparent"
              }`}
            >
              <div className="w-6 text-center">
                <i className={`fa-solid ${tab.icon}`}></i>
              </div>
              <span>{tab.label}</span>
            </button>
          ))}
        </aside>

        {/* MAIN */}
        <main className="flex-1 bg-slate-50 relative overflow-hidden flex flex-col">
          {activeTab === "none" && (
            <div className="p-8 text-center text-gray-400">No content available</div>
          )}

          {/* SUMMARY TAB */}
          {hasSummary && activeTab === "summary" && (
            <div className="w-full h-full overflow-y-auto custom-scroll p-4 md:p-8">
              <div className="max-w-3xl mx-auto bg-white rounded-2xl p-6 md:p-10 shadow-sm border border-gray-200">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-bold text-gray-800">High-Yield Notes</h2>
                  <button
                    onClick={() => setIsEli5(!isEli5)}
                    className={`text-xs font-bold px-3 py-1 rounded-full border transition ${
                      isEli5
                        ? "bg-blue-100 text-blue-600 border-blue-200"
                        : "bg-gray-100 text-gray-500 border-gray-200"
                    }`}
                  >
                    ELI5 {isEli5 ? "ON" : "OFF"}
                  </button>
                </div>

                {!loading && !fetchError && summaryCheckpointQuestions.length === 0 && (
                  <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-700 text-sm">
                    No quiz items found for this reviewer, so reading checkpoints cannot appear.
                  </div>
                )}

                {fetchError ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm">
                    {fetchError}
                  </div>
                ) : loading ? (
                  <div className="animate-pulse h-40 bg-gray-100 rounded-xl" />
                ) : summaryIsHtml ? (
                  <div
                    ref={summaryContentRef}
                    onScroll={(event) => setSummaryScrollTop(event.currentTarget.scrollTop)}
                    className={summaryHtmlClass}
                    dangerouslySetInnerHTML={{ __html: sanitizedSummaryHtml }}
                  />
                ) : (
                  <div
                    ref={summaryContentRef}
                    onScroll={(event) => setSummaryScrollTop(event.currentTarget.scrollTop)}
                    className={summaryMarkdownClass}
                  >
                    <SmartText content={summaryAsMarkdown} />
                  </div>
                )}
              </div>
            </div>
          )}

          {hasSummary &&
            activeTab === "summary" &&
            summaryCheckpointVisible &&
            summaryCheckpointCurrent && (
              <div className="absolute inset-0 z-40 bg-black/35 backdrop-blur-[1px] flex items-center justify-center p-4">
                <div className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-2xl p-5 md:p-6">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-blue-700">
                      Reading Checkpoint
                    </span>
                    <span className="text-xs font-semibold text-blue-700">
                      Question {summaryCheckpointIndex + 1} / {summaryCheckpointQuestions.length}
                    </span>
                  </div>

                  <div className="text-base md:text-lg font-semibold text-slate-800 mb-4 leading-relaxed">
                    <SmartText content={summaryCheckpointCurrent.prompt} />
                  </div>

                  {summaryCheckpointCurrent.options?.length > 0 ? (
                    <div className="space-y-2.5">
                      {summaryCheckpointCurrent.options.map((opt, i) => {
                        const letter = String.fromCharCode(65 + i);
                        const isSelected = summaryCheckpointSelectedOption === letter;
                        return (
                          <button
                            key={`${summaryCheckpointCurrent.id}-${letter}`}
                            disabled={summaryCheckpointStatus != null}
                            onClick={() => setSummaryCheckpointSelectedOption(letter)}
                            className={`w-full text-left rounded-xl border p-3 transition flex items-center gap-3 ${
                              isSelected
                                ? "border-blue-500 bg-blue-100"
                                : "border-blue-100 bg-white hover:border-blue-300"
                            } ${summaryCheckpointStatus != null ? "cursor-default" : ""}`}
                          >
                            <span
                              className={`w-7 h-7 rounded-md shrink-0 flex items-center justify-center text-xs font-bold ${
                                isSelected ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {letter}
                            </span>
                            <div className="text-sm md:text-base text-slate-700 flex-1 break-words">
                              <SmartText content={opt} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <textarea
                      disabled={summaryCheckpointStatus != null}
                      className="w-full p-3 rounded-xl border border-blue-200 bg-white min-h-[96px] resize-none focus:border-blue-500"
                      placeholder="Type your answer..."
                      value={summaryCheckpointTextAnswer}
                      onChange={(e) => setSummaryCheckpointTextAnswer(e.target.value)}
                    ></textarea>
                  )}

                  {summaryCheckpointStatus != null && (
                    <div
                      className={`mt-4 rounded-xl border p-3 text-sm ${
                        summaryCheckpointStatus === "correct"
                          ? "border-green-200 bg-green-50 text-green-700"
                          : "border-red-200 bg-red-50 text-red-700"
                      }`}
                    >
                      <div className="font-bold">
                        {summaryCheckpointStatus === "correct" ? "Correct!" : "Not quite."}
                      </div>
                      {summaryCheckpointStatus !== "correct" && (
                        <div className="mt-2">
                          <span className="font-semibold">Correct answer:</span>
                          <div className="mt-1 text-slate-800">
                            <CorrectAnswerNode q={summaryCheckpointCurrent} />
                          </div>
                        </div>
                      )}
                      {summaryCheckpointCurrent?.explanation && (
                        <div className="mt-2 text-slate-700">
                          <span className="font-semibold">Explanation:</span>{" "}
                          <SmartText content={summaryCheckpointCurrent.explanation} />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-4 flex justify-end gap-2">
                    {summaryCheckpointStatus == null ? (
                      <>
                        <button
                          onClick={handleSummaryCheckpointPass}
                          className="px-5 py-2.5 rounded-lg text-sm font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 transition"
                        >
                          Pass
                        </button>
                        <button
                          onClick={handleSummaryCheckpointSubmit}
                          disabled={!hasSummaryCheckpointAnswer}
                          className={`px-5 py-2.5 rounded-lg text-sm font-bold transition ${
                            hasSummaryCheckpointAnswer
                              ? "bg-blue-600 text-white hover:bg-blue-700"
                              : "bg-blue-100 text-blue-300 cursor-not-allowed"
                          }`}
                        >
                          Submit
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleSummaryCheckpointNext}
                        className="px-5 py-2.5 rounded-lg text-sm font-bold bg-slate-900 text-white hover:bg-black transition"
                      >
                        {summaryCheckpointIndex < summaryCheckpointQuestions.length - 1
                          ? "Continue Reading"
                          : "Finish Checkpoints"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

          {/* FLASHCARDS TAB */}
          {hasFlashcards && activeTab === "flashcards" && (
            <div className="w-full h-full flex flex-col items-center justify-center p-4 overflow-y-auto">
              <div className="w-full max-w-xl mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span>Card {cardIndex + 1}</span>
                    <span className="text-gray-400 font-normal text-base">/ {flashcards.length}</span>
                  </div>
                  <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
                    {Math.round(((cardIndex + 1) / flashcards.length) * 100)}% Complete
                  </span>
                </div>
                <div className="h-1.5 w-full bg-gray-200 rounded-full mb-3 overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-300"
                    style={{ width: `${((cardIndex + 1) / flashcards.length) * 100}%` }}
                  ></div>
                </div>

                {/* NUMBERED FLASHCARD NAVIGATION (1 to N) */}
                {flashcards.length > 1 && (
                  <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 py-1 max-h-24 overflow-y-auto custom-scroll">
                    {flashcards.map((_, idx) => {
                      const isActive = cardIndex === idx;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setIsFlipped(false);
                            setCardIndex(idx);
                          }}
                          className={`min-w-[34px] h-[34px] px-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 flex items-center justify-center cursor-pointer ${
                            isActive
                              ? "bg-blue-600 text-white shadow-md shadow-blue-500/30 scale-105 ring-2 ring-blue-300 ring-offset-1"
                              : "bg-white text-slate-700 border border-slate-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 shadow-sm"
                          }`}
                          title={`Go to Card ${idx + 1}`}
                          aria-label={`Go to Card ${idx + 1}`}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {loading ? (
                <div className="w-full max-w-xl h-64 bg-white rounded-3xl border border-gray-100 animate-pulse" />
              ) : flashcards.length > 0 ? (
                <>
                  <div
                    className="group w-full max-w-xl h-80 perspective-1000 cursor-pointer mb-6"
                    onClick={() => {
                      setIsFlipped(!isFlipped);
                      // ✅ Count this flip as "Study Activity"
                      if (!isFlipped) cardsReviewedRef.current += 1;
                    }}
                  >
                    <div
                      className={`relative w-full h-full transform-style-3d transition-transform duration-500 shadow-xl rounded-3xl ${
                        isFlipped ? "rotate-y-180" : ""
                      }`}
                    >
                      <div className="absolute w-full h-full bg-white rounded-3xl border border-gray-100 p-8 flex flex-col items-center justify-center backface-hidden">
                        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full mb-6 uppercase">
                          Concept
                        </span>
                        <div className="text-3xl font-bold text-gray-800 text-center w-full">
                          <SmartText content={currentCard?.front} />
                        </div>
                      </div>
                      <div className="absolute w-full h-full bg-slate-800 rounded-3xl p-8 flex flex-col items-center justify-center backface-hidden rotate-y-180 text-white">
                        <span className="text-xs font-bold text-slate-400 mb-4 uppercase">
                          Answer
                        </span>
                        <div className="text-xl font-semibold text-center text-blue-300 w-full">
                          <SmartText content={currentCard?.back} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Flip Hint */}
                  <p className="text-xs text-gray-400 mb-4 flex items-center gap-1.5 select-none">
                    <i className="fa-solid fa-rotate text-[11px]"></i> Click card to flip (Concept &amp; Answer)
                  </p>

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={cardIndex === 0}
                      className="w-11 h-11 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm flex items-center justify-center cursor-pointer"
                      onClick={() => {
                        setIsFlipped(false);
                        setCardIndex((i) => Math.max(0, i - 1));
                      }}
                      title="Previous Card"
                      aria-label="Previous Card"
                    >
                      <i className="fa-solid fa-arrow-left"></i>
                    </button>

                    <button
                      type="button"
                      className="w-12 h-12 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition shadow-sm flex items-center justify-center cursor-pointer"
                      onClick={() => {
                        setIsFlipped(false);
                        setCardIndex((i) => Math.min(i + 1, flashcards.length - 1));
                      }}
                      title="Need Review"
                      aria-label="Need Review"
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>

                    <button
                      type="button"
                      className="w-12 h-12 rounded-full bg-blue-600 text-white shadow-lg shadow-blue-600/30 hover:bg-blue-700 transition flex items-center justify-center cursor-pointer"
                      onClick={() => {
                        setIsFlipped(false);
                        setCardIndex((i) => Math.min(i + 1, flashcards.length - 1));
                      }}
                      title="Mastered"
                      aria-label="Mastered"
                    >
                      <i className="fa-solid fa-check"></i>
                    </button>

                    <button
                      type="button"
                      disabled={cardIndex === flashcards.length - 1}
                      className="w-11 h-11 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm flex items-center justify-center cursor-pointer"
                      onClick={() => {
                        setIsFlipped(false);
                        setCardIndex((i) => Math.min(i + 1, flashcards.length - 1));
                      }}
                      title="Next Card"
                      aria-label="Next Card"
                    >
                      <i className="fa-solid fa-arrow-right"></i>
                    </button>
                  </div>
                </>
              ) : (
                <div>No Flashcards</div>
              )}
            </div>
          )}

          {/* QUIZ TAB */}
          {hasQuiz && activeTab === "quiz" && (
            <div className="w-full h-full overflow-y-auto custom-scroll p-4 md:p-8">
              <div className="max-w-2xl mx-auto mt-4 pb-10">
                {loading ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200 animate-pulse h-56" />
                ) : quizQuestions.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
                    <h3 className="font-bold text-slate-800">No quiz questions</h3>
                  </div>
                ) : quizState === "intro" ? (
                  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200 text-center">
                    <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto text-2xl mb-4">
                      <i className="fa-solid fa-flag-checkered"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Mode</h2>
                    <p className="text-gray-500 mb-8">You have {quizQuestions.length} questions.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        onClick={() => startQuiz("practice")}
                        className="group flex flex-col items-center p-6 border-2 border-gray-100 hover:border-blue-500 rounded-xl hover:bg-blue-50 transition"
                      >
                        <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-3 group-hover:bg-blue-600 group-hover:text-white transition">
                          <i className="fa-solid fa-bolt"></i>
                        </div>
                        <h3 className="font-bold text-gray-800">Practice Mode</h3>
                        <p className="text-sm text-gray-500 mt-1">Immediate feedback.</p>
                      </button>
                      <button
                        onClick={() => startQuiz("exam")}
                        className="group flex flex-col items-center p-6 border-2 border-gray-100 hover:border-purple-500 rounded-xl hover:bg-purple-50 transition"
                      >
                        <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mb-3 group-hover:bg-purple-600 group-hover:text-white transition">
                          <i className="fa-solid fa-graduation-cap"></i>
                        </div>
                        <h3 className="font-bold text-gray-800">Exam Mode</h3>
                        <p className="text-sm text-gray-500 mt-1">Results at the end.</p>
                      </button>
                    </div>
                  </div>
                ) : quizState === "finished" ? (
                  <div className="flex flex-col gap-6">
                    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200 text-center">
                      <div className="mb-2 uppercase tracking-wide text-xs font-bold text-gray-400">
                        Quiz Complete
                      </div>
                      <div className="text-5xl font-bold text-gray-900 mb-4">
                        {score} / {quizQuestions.length}
                      </div>
                      <p className="text-gray-500 mb-6">
                        Mode: {quizModeType === "practice" ? "Practice" : "Exam"}
                      </p>
                      <button
                        onClick={() => setQuizState("intro")}
                        className="bg-gray-900 text-white px-8 py-3 rounded-xl font-bold shadow-lg hover:bg-black transition flex items-center gap-2 mx-auto"
                      >
                        <i className="fa-solid fa-rotate-right"></i> Restart
                      </button>
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                      <h3 className="font-bold text-lg mb-4 text-gray-800 border-b pb-2">
                        Review Answers
                      </h3>
                      <div className="space-y-6">
                        {quizQuestions.map((q, idx) => {
                          const userAns = userAnswers[idx];
                          const isCorrect = checkCorrectness(q, userAns);
                          const renderUserAnswer = () => {
                            if (!userAns) return <span>(Skipped)</span>;
                            if (q.options?.length > 0) {
                              const selectedIndex = String(userAns).toUpperCase().charCodeAt(0) - 65;
                              const optText = q.options[selectedIndex] || "";
                              return (
                                <div className="flex items-start gap-2">
                                  <span className="font-mono font-bold">
                                    {String(userAns).toUpperCase()}:
                                  </span>
                                  <div className="flex-1">
                                    <SmartText content={optText} />
                                  </div>
                                </div>
                              );
                            }
                            return <SmartText content={String(userAns)} />;
                          };
                          return (
                            <div
                              key={idx}
                              className={`p-4 rounded-xl border ${
                                isCorrect
                                  ? "bg-green-50 border-green-100"
                                  : "bg-red-50 border-red-100"
                              }`}
                            >
                              <div className="flex gap-3 mb-2">
                                <span
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                                    isCorrect ? "bg-green-600 text-white" : "bg-red-600 text-white"
                                  }`}
                                >
                                  {idx + 1}
                                </span>
                                <h4 className="font-semibold text-gray-900">
                                  <SmartText content={q.prompt} />
                                </h4>
                              </div>
                              <div className="ml-9 text-sm space-y-2">
                                <div className="flex flex-col gap-1">
                                  <span className="font-bold text-gray-500">Your Answer:</span>
                                  <div className={isCorrect ? "text-green-700" : "text-red-700"}>
                                    {renderUserAnswer()}
                                  </div>
                                </div>
                                {!isCorrect && (
                                  <div className="flex flex-col gap-1">
                                    <span className="font-bold text-gray-500">Correct Answer:</span>
                                    <div className="text-gray-800 font-medium">
                                      <CorrectAnswerNode q={q} />
                                    </div>
                                  </div>
                                )}
                                {!isCorrect && q?.explanation && (
                                  <div className="flex flex-col gap-1">
                                    <span className="font-bold text-gray-500">Explanation:</span>
                                    <div className="text-gray-700">
                                      <SmartText content={q.explanation} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Top Progress & Timer Header */}
                    <div 
                      role="region"
                      aria-label="Quiz Progress and Timer"
                      className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex justify-between items-center mb-6 sticky top-0 z-20"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          aria-label={`Question ${quizIndex + 1} of ${quizQuestions.length}`}
                          className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-extrabold shadow-sm text-sm"
                        >
                          {quizIndex + 1}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">Question</span>
                          <span className="text-sm font-extrabold text-slate-800">
                            {quizIndex + 1} <span className="text-slate-500 font-normal">of {quizQuestions.length}</span>
                          </span>
                        </div>
                      </div>

                      <div 
                        role="progressbar"
                        aria-valuenow={quizIndex + 1}
                        aria-valuemin={1}
                        aria-valuemax={quizQuestions.length}
                        aria-label="Quiz completion progress"
                        className="flex-1 mx-6 h-3 bg-slate-100 rounded-full overflow-hidden p-0.5 border border-slate-200 hidden sm:block"
                      >
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-300"
                          style={{ width: `${((quizIndex + 1) / quizQuestions.length) * 100}%` }}
                        ></div>
                      </div>

                      <div 
                        aria-label={`Time remaining: ${timeLeft} seconds`}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl font-mono font-bold text-sm border ${
                          timeLeft < 10 
                            ? "bg-red-50 text-red-700 border-red-200 animate-pulse" 
                            : "bg-slate-50 text-slate-700 border-slate-200"
                        }`}
                      >
                        <i className={`fa-solid fa-stopwatch ${timeLeft < 10 ? 'text-red-500' : 'text-slate-400'}`} aria-hidden="true"></i>
                        <span>{timeLeft}s</span>
                      </div>
                    </div>

                    {/* Question and Multiple Choice Card */}
                    <div 
                      role="region"
                      aria-label="Active Quiz Question"
                      className="bg-white p-5 md:p-8 rounded-2xl shadow-sm border border-slate-200 mb-6 relative"
                    >
                      <div className="flex items-center justify-between gap-4 mb-4 border-b border-slate-100 pb-3">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-700 bg-blue-50 px-3 py-1 rounded-full border border-blue-100 uppercase tracking-wide">
                          <i className="fa-solid fa-circle-question" aria-hidden="true"></i>
                          {quizModeType === "practice" ? "Practice Mode" : "Exam Mode"}
                        </span>
                        <span className="text-xs text-slate-500 font-medium">
                          Select the best option
                        </span>
                      </div>

                      {/* Main Question Heading */}
                      <h2 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-6 leading-relaxed break-words tracking-tight [overflow-wrap:anywhere] [word-break:break-word]">
                        <SmartText content={currentQuiz?.prompt} />
                      </h2>

                      {currentQuiz?.options.length > 0 ? (
                        <div 
                          role="radiogroup" 
                          aria-label="Answer options"
                          className="space-y-3"
                        >
                          {currentQuiz.options.map((option, i) => {
                            const optId = String.fromCharCode(65 + i);
                            let containerStyle = "border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50/40 text-slate-800";
                            let badgeStyle = "bg-slate-100 text-slate-700 border-slate-300";
                            let iconIndicator = null;
                            const isSelected = selectedQuizOption === optId;

                            if (quizModeType === "practice" && quizState === "feedback") {
                              const correctIdx = getCorrectIndexIfLetter(currentQuiz);
                              const isActuallyCorrect =
                                correctIdx != null
                                  ? i === correctIdx
                                  : normalizeForCompare(stripChoiceLetter(currentQuiz.options[i])) ===
                                    normalizeForCompare(stripChoiceLetter(currentQuiz.answer));

                              if (isActuallyCorrect) {
                                containerStyle = "border-emerald-500 bg-emerald-50/80 text-emerald-950 font-semibold shadow-xs";
                                badgeStyle = "bg-emerald-600 text-white border-emerald-600";
                                iconIndicator = (
                                  <span className="text-emerald-700 font-bold flex items-center gap-1 text-xs shrink-0 bg-white/80 px-2 py-1 rounded-md border border-emerald-200" aria-label="Correct answer">
                                    <i className="fa-solid fa-circle-check text-emerald-600" aria-hidden="true"></i> Correct
                                  </span>
                                );
                              } else if (isSelected && !isActuallyCorrect) {
                                containerStyle = "border-red-400 bg-red-50/80 text-red-950 shadow-xs";
                                badgeStyle = "bg-red-600 text-white border-red-600";
                                iconIndicator = (
                                  <span className="text-red-700 font-bold flex items-center gap-1 text-xs shrink-0 bg-white/80 px-2 py-1 rounded-md border border-red-200" aria-label="Incorrect answer">
                                    <i className="fa-solid fa-circle-xmark text-red-600" aria-hidden="true"></i> Your Choice
                                  </span>
                                );
                              } else {
                                containerStyle = "border-slate-100 bg-slate-50/50 opacity-60 text-slate-400";
                                badgeStyle = "bg-slate-100 text-slate-400 border-slate-200";
                              }
                            } else if (isSelected) {
                              containerStyle = "border-blue-600 bg-blue-50/90 text-blue-950 shadow-sm ring-2 ring-blue-500/20";
                              badgeStyle = "bg-blue-600 text-white border-blue-600 shadow-xs";
                              iconIndicator = (
                                <span className="text-blue-700 text-xs font-bold shrink-0 bg-white px-2 py-1 rounded-md border border-blue-200" aria-hidden="true">
                                  <i className="fa-solid fa-check"></i> Selected
                                </span>
                              );
                            }

                            return (
                              <button
                                key={optId}
                                role="radio"
                                aria-checked={isSelected}
                                aria-label={`Option ${optId}: ${option}`}
                                disabled={quizState === "feedback"}
                                onClick={() => setSelectedQuizOption(optId)}
                                className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between gap-4 focus-visible:ring-4 focus-visible:ring-blue-400 focus-visible:outline-none ${containerStyle}`}
                              >
                                <div className="flex items-center gap-3.5 flex-1 min-w-0">
                                  <div
                                    className={`w-9 h-9 text-sm rounded-lg flex items-center justify-center font-extrabold border shrink-0 transition-colors ${badgeStyle}`}
                                  >
                                    {optId}
                                  </div>
                                  <div className="font-semibold text-sm md:text-base flex-1 break-words leading-relaxed">
                                    <SmartText content={option} />
                                  </div>
                                </div>
                                {iconIndicator}
                              </button>
                            );
                          })}
                          {quizModeType === "practice" && <FeedbackPanel />}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <label htmlFor="text-quiz-answer" className="block text-sm font-bold text-slate-700">
                            Type your written response:
                          </label>
                          <textarea
                            id="text-quiz-answer"
                            disabled={quizState === "feedback"}
                            className="w-full p-4 rounded-xl border-2 text-base md:text-lg min-h-[120px] transition resize-none border-slate-300 bg-slate-50 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 text-slate-900"
                            placeholder="Type your answer clearly here..."
                            value={textAnswer}
                            onChange={(e) => setTextAnswer(e.target.value)}
                          ></textarea>
                          {quizModeType === "practice" && <FeedbackPanel />}
                        </div>
                      )}
                    </div>

                    {/* Bottom Primary Action Bar */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <p className="text-xs text-slate-500 font-medium">
                        {quizModeType === "practice" 
                          ? "Submit your choice to view feedback and explanations." 
                          : "Your answers are saved automatically as you advance."}
                      </p>
                      {quizModeType === "practice" ? (
                        quizState === "feedback" ? (
                          <button
                            className="w-full sm:w-auto bg-slate-900 text-white px-8 py-3.5 rounded-xl font-extrabold shadow-md hover:bg-black transition flex items-center justify-center gap-2 focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:outline-none"
                            onClick={handlePracticeNext}
                            aria-label={quizIndex < quizQuestions.length - 1 ? "Advance to next question" : "Finish quiz and see results"}
                          >
                            <span>{quizIndex < quizQuestions.length - 1 ? "Next Question" : "See Results"}</span>
                            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                          </button>
                        ) : (
                          <button
                            disabled={!hasSelectedAnswer}
                            className={`w-full sm:w-auto px-8 py-3.5 rounded-xl font-extrabold shadow-md transition flex items-center justify-center gap-2 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none ${
                              hasSelectedAnswer
                                ? "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/20"
                                : "bg-slate-200 text-slate-400 cursor-not-allowed"
                            }`}
                            onClick={handlePrimaryAction}
                            aria-label="Submit your answer"
                          >
                            <span>Submit Answer</span>
                            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                          </button>
                        )
                      ) : (
                        <button
                          disabled={!hasSelectedAnswer}
                          className={`w-full sm:w-auto px-8 py-3.5 rounded-xl font-extrabold shadow-md transition flex items-center justify-center gap-2 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none ${
                            hasSelectedAnswer
                              ? "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/20"
                              : "bg-slate-200 text-slate-400 cursor-not-allowed"
                          }`}
                          onClick={handlePrimaryAction}
                          aria-label={quizIndex < quizQuestions.length - 1 ? "Confirm answer and advance to next question" : "Complete exam"}
                        >
                          <span>{quizIndex < quizQuestions.length - 1 ? "Next Question" : "Submit Exam"}</span>
                          <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default StudyMode;
